import { uuidV4 } from './ids';
import { HISTORY_SESSION_IDLE_GAP_MS } from './types';

/**
 * Listening-session ids for history events, ported from PlayerProvider
 * (`_ensureHistorySessionForBook`, `_historySessionIdForBook`,
 * `player_provider.dart:2404-2469`). A session id is reused when playback of
 * the same book resumes within 15 minutes of the last pause. In memory only.
 */
export class HistorySessionTracker {
  private activeSessionId: string | null = null;
  private activeBookId: string | null = null;
  private playbackActive = false;
  private lastEndedAt: number | null = null;

  constructor(
    private readonly now: () => number = Date.now,
    private readonly reuseGapMs: number = HISTORY_SESSION_IDLE_GAP_MS,
  ) {}

  /** On play: reuse the current session or start a new one. */
  ensureSessionFor(bookId: string): string {
    const now = this.now();
    if (this.activeBookId === bookId && this.activeSessionId) {
      if (this.playbackActive) {
        this.lastEndedAt = null;
        return this.activeSessionId;
      }
      if (this.lastEndedAt != null && now - this.lastEndedAt <= this.reuseGapMs) {
        this.playbackActive = true;
        this.lastEndedAt = null;
        return this.activeSessionId;
      }
    }
    this.activeBookId = bookId;
    this.playbackActive = true;
    this.lastEndedAt = null;
    this.activeSessionId = `local-${bookId}-${now * 1000}-${uuidV4()}`;
    return this.activeSessionId;
  }

  /**
   * For seeks, chapters, speed, sleep and bookmarks: the active session if it
   * belongs to this book and is playing or paused for at most 15 minutes;
   * otherwise null (the event is grouped by time instead).
   */
  sessionIdFor(bookId: string): string | null {
    const id = bookId.trim();
    if (!id || this.activeBookId !== id || !this.activeSessionId) return null;
    if (this.playbackActive) return this.activeSessionId;
    if (this.lastEndedAt == null) return null;
    if (this.now() - this.lastEndedAt <= this.reuseGapMs) return this.activeSessionId;
    this.clear();
    return null;
  }

  /** After a pause/stop has been logged. */
  markPlaybackEnded(): void {
    this.playbackActive = false;
    this.lastEndedAt = this.now();
  }

  /** A new book load (or account switch) starts fresh. */
  clear(): void {
    this.activeSessionId = null;
    this.activeBookId = null;
    this.lastEndedAt = null;
    this.playbackActive = false;
  }
}

/** The app-wide tracker used by the player bridge and bookmark logging. */
export const historySessions = new HistorySessionTracker();

/** Session id to attach to an event for `bookId` (Flutter `historySessionIdForBook`). */
export function historySessionIdForBook(bookId: string): string | null {
  return historySessions.sessionIdFor(bookId);
}
